// The Scheduling page's reads and actions: ONE copy, used by the desktop Scheduling page
// (pages/SchedulingPage.jsx) and the phone (mobile/screens/SchedulingScreen.jsx).
// docs/mobile-pwa-plan.md "One copy of every action". Moved from SchedulingPage word for word;
// pages/SchedulingPage.actions.test.jsx pins the prompts and requests.
//
// Actions take `ui` (features/desktopUi.js | mobile/ui/phoneUi.js): confirm, alert. The desktop's
// window.alert / window.confirm are kept word for word.
import config from '../../config.js';
import { apiErrorMessage } from '../../utils/apiError';
import { fetchAllJobPages, fetchJobsPage } from '../../utils/jobPages';
import { STATIONS, formatDisplayDate } from './scheduleRules';

const API_BASE = config.API_BASE_URL;
const noop = () => {};
const enc = encodeURIComponent;

// --- Reads ------------------------------------------------------------------------------------------

/** Laser tasks gain available_before_task / available_after_task (sheets) from /laser_timeline.
 *  A failed timeline read leaves them as they are. */
async function withLaserTimeline(tasks) {
  let usageMap = {};
  try {
    const usageRes = await fetch(`${API_BASE}/laser_timeline`);
    if (usageRes.ok) usageMap = await usageRes.json();
  } catch (err) {
    console.error('Error fetching laser timeline:', err);
  }
  tasks.forEach((t) => {
    if (t.workstation === 'Laser') {
      const entry = usageMap[t.task_id];
      if (entry) {
        t.available_before_task = entry.available_before_task;
        t.available_after_task = entry.available_after_task;
      }
    }
  });
  return tasks;
}

const openTasks = (rows) => rows.filter((t) => t.workstation !== 'Shipping' && t.task_status !== 'Completed');

/** One station's open tasks (180 days ahead; Shipping and Completed left out), then its locked days.
 *  onTasks(tasks) runs as soon as the tasks are read (before the locks). Resolves to
 *  {tasks, lockedDates: Set | null (the locks couldn't be read), failed: true when the queue
 *  itself was refused}. */
export async function loadStationQueue(station, { onTasks = noop } = {}) {
  const res = await fetch(`${API_BASE}/queue/${station}?horizon_days=180`);
  if (!res.ok) return { tasks: [], lockedDates: null, failed: true, status: res.status };
  const tasks = await withLaserTimeline(openTasks(await res.json()));
  onTasks(tasks);
  let lockedDates = null;
  try {
    const lockRes = await fetch(`${API_BASE}/schedule/locks?station=${enc(station)}`);
    if (lockRes.ok) {
      const locks = await lockRes.json();
      lockedDates = new Set((locks || []).map((l) => l.lock_date));
    }
  } catch (lockErr) {
    console.error('Error fetching schedule locks:', lockErr);
  }
  return { tasks, lockedDates, failed: false };
}

/** Every station's open tasks, one station after another, each tagged `station`. */
export async function loadAllStationsTasks(stations = STATIONS) {
  const combined = [];
  for (const st of stations) {
    // eslint-disable-next-line no-await-in-loop
    const res = await fetch(`${API_BASE}/queue/${st}?horizon_days=180`);
    if (res.ok) {
      // eslint-disable-next-line no-await-in-loop
      const arr = await res.json();
      arr.forEach((item) => {
        item.station = st;
      });
      combined.push(...arr);
    }
  }
  return withLaserTimeline(openTasks(combined));
}

/** GET /shifts/config (null when refused). */
export async function loadShiftConfig() {
  const res = await fetch(`${API_BASE}/shifts/config`);
  return res.ok ? res.json() : null;
}

/** A job's tasks for scheduling: the job row + its progress tasks, or null when the job isn't read. */
export async function loadJobSchedule(jobId) {
  const res = await fetch(`${API_BASE}/jobs/${jobId}/progress`);
  if (!res.ok) {
    console.error('Failed to fetch job progress:', res.status);
    return null;
  }
  const progressData = await res.json();
  const jobRes = await fetch(`${API_BASE}/jobs/${jobId}`);
  let jobDetail = {};
  if (jobRes.ok) jobDetail = await jobRes.json();
  return { ...jobDetail, tasks: progressData.tasks };
}

/** What the person typed (a master job #, usually) as a job_id: the search's exact master-number
 *  match, else its only result, else the input itself (tried as a job id). Summary rows, page by
 *  page (GET /jobs in one response can pass Lambda's 6 MB limit). */
export async function resolveJobInput(input) {
  try {
    const searchResults = await fetchAllJobPages((page) =>
      fetchJobsPage(API_BASE, new URLSearchParams({ search_term: input, summary: 'true' }), page));
    const masterMatch = searchResults.find((j) => String(j.master_job_num) === input);
    if (masterMatch) return masterMatch.job_id;
    if (searchResults.length === 1) return searchResults[0].job_id;
  } catch (err) {
    console.warn('Job search failed, attempting direct ID lookup', err);
  }
  return input;
}

// --- Actions -----------------------------------------------------------------------------------------

/** Pin a task to a day (PATCH manual_schedule). Resolves {done}. */
export async function overrideTaskDate({ taskId, date, ui }) {
  if (!(await ui.confirm(`Override this task's schedule to ${date}?`))) return { done: false };
  try {
    const res = await fetch(`${API_BASE}/tasks/${taskId}/manual_schedule`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ scheduled_date: date }),
    });
    if (!res.ok) {
      await ui.alert('Failed to override schedule: ' + res.status);
      return { done: false };
    }
    await ui.alert('Task override successful.');
    return { done: true };
  } catch (err) {
    console.error('Error overriding schedule:', err);
    await ui.alert('Error overriding schedule.');
    return { done: false };
  }
}

/** Give a task back to the auto-scheduler (PATCH clear_override). Resolves {done}. */
export async function clearTaskOverride({ taskId, ui }) {
  if (!(await ui.confirm('Remove manual override? The task will be rescheduled automatically.'))) return { done: false };
  try {
    const res = await fetch(`${API_BASE}/tasks/${taskId}/clear_override`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      await ui.alert('Failed to clear override: ' + res.status);
      return { done: false };
    }
    await ui.alert('Override removed. Task will be auto-scheduled.');
    return { done: true };
  } catch (err) {
    console.error('Error clearing override:', err);
    await ui.alert('Error clearing override.');
    return { done: false };
  }
}

/** Lock or unlock one station's day (POST /schedule/lock_day). afterSave() runs between the save and
 *  the alert (the desktop re-reads the queue there). Resolves {done}. */
export async function setDayLock({ station, date, locked, ui, afterSave = noop }) {
  try {
    const res = await fetch(`${API_BASE}/schedule/lock_day`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ date, locked, station }),
    });
    if (!res.ok) throw new Error(await apiErrorMessage(res, 'Failed to update lock status'));
    afterSave();
    await ui.alert(`Schedule for ${station} on ${date} is now ${locked ? 'LOCKED' : 'UNLOCKED'}.`);
    return { done: true };
  } catch (err) {
    console.error(err);
    await ui.alert('Error updating schedule lock: ' + err.message);
    return { done: false };
  }
}

/** Save a day's order: these task ids, first to last, pinned to their positions (the desktop's drag,
 *  the phone's up / down). A refusal is said through ui.alert. Resolves {done}; the caller re-reads
 *  either way. */
export async function saveDayOrder({ station, date, taskIds, ui }) {
  try {
    const res = await fetch(`${API_BASE}/queue/${enc(station)}/reorder`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ scheduled_date: date, task_ids: taskIds }),
    });
    if (!res.ok) throw new Error(await apiErrorMessage(res, 'Failed to save new order'));
    return { done: true };
  } catch (err) {
    console.error(err);
    await ui.alert('Error saving manual order: ' + err.message);
    return { done: false };
  }
}

/** Back to the auto order for a day (DELETE reorder). Resolves {done}. */
export async function clearDayOrder({ station, dateObj, date, ui }) {
  if (!(await ui.confirm(`Reset all manual ordering for ${station} on ${formatDisplayDate(dateObj)}?`))) return { done: false };
  try {
    const res = await fetch(`${API_BASE}/queue/${enc(station)}/reorder?scheduled_date=${date}`, { method: 'DELETE' });
    if (!res.ok) throw new Error(await apiErrorMessage(res, 'Failed to clear order'));
    return { done: true };
  } catch (err) {
    console.error(err);
    await ui.alert('Error clearing manual order: ' + err.message);
    return { done: false };
  }
}

/** A day's second shift (a per-day override): PUT. A refusal is said through ui.alert. Resolves {done}. */
export async function saveSecondShift({ station, date, enabled, hours, ui }) {
  try {
    const res = await fetch(`${API_BASE}/shifts/overrides/${enc(station)}/${date}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ second_shift_enabled: !!enabled, second_shift_hours: Number(hours) || 0 }),
    });
    if (!res.ok) throw new Error(await apiErrorMessage(res, 'Failed to update second shift'));
    return { done: true };
  } catch (err) {
    console.error(err);
    await ui.alert('Error updating second shift: ' + err.message);
    return { done: false };
  }
}

export function secondShiftDefaultQuestion(station, effective) {
  return `Apply Second Shift = ${effective.enabled ? `${effective.hours}h` : 'off'} as the global default for ${station} on every weekday until changed?`;
}

/** The day's second shift as the station's weekday default; the day's override then goes. Ask
 *  secondShiftDefaultQuestion first. A refusal is said through ui.alert. Resolves {done}. */
export async function saveSecondShiftDefault({ station, date, effective, ui }) {
  try {
    const dRes = await fetch(`${API_BASE}/shifts/defaults/${enc(station)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ second_shift_enabled: !!effective.enabled, second_shift_hours: Number(effective.hours) || 0 }),
    });
    if (!dRes.ok) throw new Error(await apiErrorMessage(dRes, 'Failed to save default'));
    // Clear the day's override so the new default applies cleanly to this date.
    await fetch(`${API_BASE}/shifts/overrides/${enc(station)}/${date}`, { method: 'DELETE' });
    return { done: true };
  } catch (err) {
    console.error(err);
    await ui.alert('Error saving default: ' + err.message);
    return { done: false };
  }
}

/** Drop a day's second-shift override (back to the default). A refusal is said through ui.alert.
 *  Resolves {done}. */
export async function clearSecondShiftOverride({ station, date, ui }) {
  try {
    const res = await fetch(`${API_BASE}/shifts/overrides/${enc(station)}/${date}`, { method: 'DELETE' });
    if (!res.ok) throw new Error(await apiErrorMessage(res, 'Failed to clear override'));
    return { done: true };
  } catch (err) {
    console.error(err);
    await ui.alert('Error clearing override: ' + err.message);
    return { done: false };
  }
}
